import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { OrderStatus, OrderWhereInput } from "../../../generated/graphql";
import { useSearchParams } from "react-router-dom";
import orderListFiltersToQuery from "../utils/orderListFiltersToQuery";

export interface DateFilter {
  start?: Date;
  end?: Date;
}

export interface OrderListFilters {
  search?: string;
  isArchived?: boolean;
  references?: string[];
  clinicCodes?: string[];
  doctors?: string[];
  providers?: string[];
  statuses?: OrderStatus[];
  createdAt?: DateFilter;
  deliveryDate?: DateFilter;
  lastShippingDate?: DateFilter;
}

interface IOrderListFilterContext extends OrderListFilters {
  page: number;
  setPage: (page: number) => void;

  clearFilters: () => void;
  setFilters: (filters: OrderListFilters) => void;

  setSearch: (search?: string) => void;
  setIsArchived: (isArchived?: boolean) => void;

  setReferences: (refereces?: string[]) => void;
  setClinicCodes: (clinicCodes?: string[]) => void;
  setDoctors: (doctors?: string[]) => void;
  setProviders: (providers?: string[]) => void;

  setStatuses: (statuses?: OrderStatus[]) => void;

  setCreatedAt: (filter?: DateFilter) => void;
  setDeliveryDate: (filter?: DateFilter) => void;
  setLastShippingDate: (filter?: DateFilter) => void;

  getFilterQuery: () => OrderWhereInput;

  getAllFilters: () => OrderListFilters;
}

export const OrderListFilterContext = createContext<
  IOrderListFilterContext | undefined
>(undefined);

const isStringDate = (value: string) =>
  new Date(value).toString() !== "Invalid Date";

const isOrderStatus = (value: unknown): value is OrderStatus =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Object.values(OrderStatus).includes(value as any);

const getDateFilterFromSearchParams = (
  searchParams: URLSearchParams,
  key: string
) => {
  const startDate = searchParams.get(`${key}Start`);
  const endDate = searchParams.get(`${key}End`);

  return {
    start:
      startDate && isStringDate(startDate) ? new Date(startDate) : undefined,
    end: endDate && isStringDate(endDate) ? new Date(endDate) : undefined,
  };
};

const getBooleanFromSearchParams = (
  searchParams: URLSearchParams,
  key: string
) => {
  const value = searchParams.get(key);

  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
};

const setArraySearchParams = ({
  searchParams,
  array = [],
  key,
}: {
  searchParams: URLSearchParams;
  array?: string[];
  key: string;
}) => {
  searchParams.delete(key);
  array.forEach(v => searchParams.append(key, v));
};

export const OrderListFilterProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(1);

  const [search, setSearch] = useState<string>();
  const [isArchived, setIsArchived] = useState<boolean>();
  const [references, setReferences] = useState<string[]>();
  const [clinicCodes, setClinicCodes] = useState<string[]>();
  const [doctors, setDoctors] = useState<string[]>();
  const [providers, setProviders] = useState<string[]>();
  const [statuses, setStatuses] = useState<OrderStatus[]>();
  const [createdAt, setCreatedAt] = useState<DateFilter>();
  const [deliveryDate, setDeliveryDate] = useState<DateFilter>();
  const [lastShippingDate, setLastShippingDate] = useState<DateFilter>();

  useEffect(() => {
    const searchParamPage = Number(searchParams.get("page"));
    if (Number.isNaN(searchParamPage)) {
      setSearchParams(prevSearchParams => {
        prevSearchParams.delete("page");
        return prevSearchParams;
      });
    } else {
      setPage(searchParamPage === 0 ? 1 : searchParamPage);
    }

    const parsedSearchParams = {
      search: searchParams.get("search") ?? "",
      isArchived: getBooleanFromSearchParams(searchParams, "isArchived"),

      references: searchParams.getAll("references"),
      clinicCodes: searchParams.getAll("clinicCodes"),
      doctors: searchParams.getAll("doctors"),
      providers: searchParams.getAll("providers"),
      statuses: searchParams.getAll("statuses").filter(isOrderStatus),

      createdAt: getDateFilterFromSearchParams(searchParams, "createdAt"),
      deliveryDate: getDateFilterFromSearchParams(searchParams, "deliveryDate"),
      lastShippingDate: getDateFilterFromSearchParams(
        searchParams,
        "lastShippingDate"
      ),
    };

    setSearch(parsedSearchParams.search);
    setIsArchived(parsedSearchParams.isArchived);
    setReferences(parsedSearchParams.references);
    setClinicCodes(parsedSearchParams.clinicCodes);
    setDoctors(parsedSearchParams.doctors);
    setProviders(parsedSearchParams.providers);
    setStatuses(parsedSearchParams.statuses);
    setCreatedAt(parsedSearchParams.createdAt);
    setDeliveryDate(parsedSearchParams.deliveryDate);
    setLastShippingDate(parsedSearchParams.lastShippingDate);
  }, []);

  useEffect(() => {
    setSearchParams(prevSearchParams => {
      if (page === 1) prevSearchParams.delete("page");
      else prevSearchParams.set("page", String(page));
      return prevSearchParams;
    });
  }, [page]);

  useEffect(() => {
    setSearchParams(previousSearchParams => {
      setPage(1);

      if (search) previousSearchParams.set("search", search);
      else previousSearchParams.delete("search");

      if (isArchived === undefined) previousSearchParams.delete("isArchived");
      else previousSearchParams.set("isArchived", String(isArchived));

      setArraySearchParams({
        searchParams: previousSearchParams,
        key: "references",
        array: references,
      });
      setArraySearchParams({
        searchParams: previousSearchParams,
        key: "clinicCodes",
        array: clinicCodes,
      });
      setArraySearchParams({
        searchParams: previousSearchParams,
        key: "doctors",
        array: doctors,
      });
      setArraySearchParams({
        searchParams: previousSearchParams,
        key: "providers",
        array: providers,
      });
      setArraySearchParams({
        searchParams: previousSearchParams,
        key: "statuses",
        array: statuses,
      });

      if (createdAt?.start) {
        previousSearchParams.set(
          "createdAtStart",
          createdAt?.start.toISOString()
        );
      } else {
        previousSearchParams.delete("createdAtStart");
      }
      if (createdAt?.end)
        previousSearchParams.set("createdAtEnd", createdAt?.end.toISOString());

      if (deliveryDate?.start) {
        previousSearchParams.set(
          "deliveryDateStart",
          deliveryDate?.start.toISOString()
        );
      } else {
        previousSearchParams.delete("deliveryDateStart");
      }
      if (deliveryDate?.end)
        previousSearchParams.set(
          "deliveryDateEnd",
          deliveryDate?.end.toISOString()
        );

      if (lastShippingDate?.start) {
        previousSearchParams.set(
          "lastShippingDateStart",
          lastShippingDate?.start.toISOString()
        );
      } else {
        previousSearchParams.delete("lastShippingDateStart");
      }
      if (lastShippingDate?.end)
        previousSearchParams.set(
          "lastShippingDateEnd",
          lastShippingDate?.end.toISOString()
        );

      return previousSearchParams;
    });
  }, [
    search,
    isArchived,
    references,
    clinicCodes,
    doctors,
    providers,
    statuses,
    createdAt,
    deliveryDate,
    lastShippingDate,
  ]);

  return (
    <OrderListFilterContext.Provider
      value={{
        page,
        setPage,

        clearFilters: () => {
          setSearch(undefined);
          setIsArchived(undefined);
          setReferences(undefined);
          setClinicCodes(undefined);
          setDoctors(undefined);
          setProviders(undefined);
          setStatuses(undefined);
          setCreatedAt(undefined);
          setDeliveryDate(undefined);
          setLastShippingDate(undefined);
        },

        setFilters: (filter: OrderListFilters) => {
          setSearch(filter.search);
          setIsArchived(filter.isArchived);
          setReferences(filter.references);
          setClinicCodes(filter.clinicCodes);
          setDoctors(filter.doctors);
          setProviders(filter.providers);
          setStatuses(filter.statuses);
          setCreatedAt(filter.createdAt);
          setDeliveryDate(filter.deliveryDate);
          setLastShippingDate(filter.lastShippingDate);
        },

        search,
        isArchived,
        references,
        clinicCodes,
        doctors,
        providers,
        statuses,
        createdAt,
        deliveryDate,
        lastShippingDate,

        setSearch,
        setIsArchived,
        setReferences,
        setClinicCodes,
        setDoctors,
        setProviders,
        setStatuses,
        setCreatedAt,
        setDeliveryDate,
        setLastShippingDate,

        getFilterQuery: () =>
          orderListFiltersToQuery({
            isArchived,
            references,
            clinicCodes,
            doctors,
            providers,
            statuses,
            createdAt,
            deliveryDate,
            lastShippingDate,
          }),

        getAllFilters: () => ({
          search,
          isArchived,
          references,
          clinicCodes,
          doctors,
          providers,
          statuses,
          createdAt,
          deliveryDate,
          lastShippingDate,
        }),
      }}
    >
      {children}
    </OrderListFilterContext.Provider>
  );
};

export const useOrderListFilter = () => {
  const context = useContext(OrderListFilterContext);
  if (context === undefined) {
    throw new Error(
      "useOrderListFilter must be used within a OrderListFilterProvider"
    );
  }
  return context;
};
